import React, { useState, useMemo } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Button, Input, DatePicker, Table, Modal, Tag, Tooltip, message, Progress, Card, Statistic } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import StandardLayout from '../../../../components/Layout/StandardLayout';
import { useMassMessages, useCancelMassMessage, useArchiveMassMessage, useMerchantPlan, useMassMessageMetrics } from '../../../../hooks/query/useMassMessages';
import { formatMessagePreview, formatDateTime } from './utils';
import './styles.scss';

dayjs.extend(utc);
dayjs.extend(tz);

const STATUS_TAG_CONFIG = {
  SCHEDULED: { color: 'blue', label: 'Scheduled' },
  PROCESSING: { color: 'processing', label: 'Processing' },
  PAUSED: { color: 'warning', label: 'Paused' },
  COMPLETED: { color: 'success', label: 'Completed' },
  CANCELING: { color: 'warning', label: 'Canceling' },
  CANCELED: { color: 'error', label: 'Canceled' },
  CANCELLED: { color: 'error', label: 'Canceled' },
  DELETED: { color: 'error', label: 'Deleted' },
};

const TERMINAL_STATUSES = new Set(['COMPLETED', 'CANCELED', 'CANCELLED']);

const MassMessaging2 = () => {
  const history = useHistory();
  const cancelMutation = useCancelMassMessage();
  const archiveMutation = useArchiveMassMessage();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [cancelTarget, setCancelTarget] = useState(null);

  // Fetch all messages (large limit for client-side filtering)
  const { data, isLoading } = useMassMessages({ offset: 0, limit: 500 });
  const allMessages = data?.results || data?.data || [];

  // Fetch metrics for current month
  const startOfMonth = dayjs().startOf('month').toISOString();
  const endOfMonth = dayjs().endOf('month').toISOString();
  const { data: metricsData } = useMassMessageMetrics({
    status: 'COMPLETED',
    scheduledAfter: startOfMonth,
    scheduledBefore: endOfMonth,
  });

  // Fetch merchant plan for mass messaging limit
  const { data: planData } = useMerchantPlan();
  const massMessagingFeature = (planData?.features || []).find(f => f.code === 'mass_messaging');
  const planLimit = massMessagingFeature ? Number(massMessagingFeature.value) || 0 : 0;

  // Client-side filtering
  const filteredMessages = useMemo(() => {
    let list = allMessages;

    if (search) {
      const term = search.toLowerCase();
      list = list.filter(m => (m.message || '').toLowerCase().includes(term));
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].startOf('day');
      const end = dateRange[1].endOf('day');
      list = list.filter(m => {
        const d = dayjs(m.scheduledDateTime);
        return d.isAfter(start) && d.isBefore(end);
      });
    }

    return list;
  }, [allMessages, search, dateRange]);

  const paginatedMessages = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredMessages.slice(start, start + pageSize);
  }, [filteredMessages, page, pageSize]);

  const messagesSent = metricsData?.totalMessagesSent ?? 0;
  const totalMassMessages = metricsData?.totalMassMessages ?? 0;
  const limitUsedPercent = planLimit > 0 ? Math.round((messagesSent / planLimit) * 100) : 0;
  const overageMessages = planLimit > 0 ? Math.max(0, messagesSent - planLimit) : 0;
  const cost = (overageMessages * 0.07).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelMutation.mutateAsync(cancelTarget);
      message.success('Mass message canceled');
      setCancelTarget(null);
    } catch {
      message.error('Failed to cancel message');
    }
  };

  const DELETABLE = new Set(['SCHEDULED', 'PROCESSING', 'PAUSED']);

  const handleDelete = record => {
    const status = (record.status || '').toUpperCase();
    const isActive = status === 'PROCESSING' || status === 'PAUSED';

    Modal.confirm({
      title: isActive ? 'Cancel Message' : 'Cancel Scheduled Message',
      content: isActive
        ? 'This message is currently being processed. Canceling it will stop delivery to any remaining recipients. This action cannot be undone.'
        : 'Are you sure you want to cancel this scheduled message? It will not be sent to your audience.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      cancelText: 'No, Keep It',
      onOk: async () => {
        try {
          await archiveMutation.mutateAsync(record.id);
          message.success('Message canceled');
        } catch {
          message.error('Failed to cancel message');
        }
      },
    });
  };

  const renderStatus = (_, record) => {
    const status = (record.status || '').toUpperCase();
    const cfg = STATUS_TAG_CONFIG[status] || { color: 'default', label: status };

    let tooltip = null;
    if (status === 'COMPLETED' && record.completedDateTime) {
      tooltip = `Completed ${formatDateTime(record.completedDateTime, record.timezone)}`;
    } else if ((status === 'CANCELED' || status === 'CANCELLED') && record.cancelledByName) {
      tooltip = `Cancelled by ${record.cancelledByName}`;
    }

    const tag = (
      <Tag color={cfg.color} style={{ fontSize: 12 }}>
        {cfg.label}
        {(status === 'PROCESSING' || status === 'PAUSED') && (
          <FontAwesomeIcon icon={['far', 'spinner-third']} spin style={{ marginLeft: 4 }} />
        )}
      </Tag>
    );

    return tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag;
  };

  const renderActions = (_, record) => {
    const status = (record.status || '').toUpperCase();
    const canDelete = DELETABLE.has(status);
    const isProcessing = status === 'PROCESSING' || status === 'PAUSED';

    if (!canDelete && !isProcessing) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isProcessing && (
          <Button
            size="small"
            danger
            ghost
            onClick={e => {
              e.stopPropagation();
              setCancelTarget(record.id);
            }}
          >
            Cancel
          </Button>
        )}
        {canDelete && (
          <Button
            size="small"
            type="text"
            danger
            onClick={e => {
              e.stopPropagation();
              handleDelete(record);
            }}
            icon={<FontAwesomeIcon icon={['far', 'trash-alt']} />}
          />
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Message Preview',
      dataIndex: 'message',
      key: 'message',
      width: '35%',
      render: text => <span className="mass-messaging-2__message-preview">{formatMessagePreview(text)}</span>,
    },
    {
      title: 'Audience',
      key: 'audienceCount',
      width: '8%',
      render: (_, record) => {
        const count = record.audience?.audienceCount;
        return count != null ? count.toLocaleString() : '—';
      },
    },
    {
      title: 'Sent',
      dataIndex: 'totalSent',
      key: 'totalSent',
      width: '6%',
      render: val => (val != null ? val.toLocaleString() : '—'),
    },
    {
      title: 'Delivery Schedule',
      key: 'schedule',
      width: '20%',
      render: (_, record) => (
        <div>
          <div>{formatDateTime(record.scheduledDateTime, record.timezone) || '—'}</div>
          {(record.associate?.name || record.createdByName) && (
            <div className="mass-messaging-2__created-by">Created by {record.associate?.name || record.createdByName}</div>
          )}
          {record.eventId && (
            <Link
              to={`/Events/event-details/${record.eventId}`}
              onClick={e => e.stopPropagation()}
              className="mass-messaging-2__event-link"
            >
              <FontAwesomeIcon icon={['far', 'calendar-star']} style={{ marginRight: 4 }} />
              View Event
            </Link>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: '8%',
      render: renderStatus,
    },
    {
      title: '',
      key: 'actions',
      width: '3%',
      align: 'center',
      render: renderActions,
    },
  ];

  return (
    <StandardLayout
      title="Mass Messages"
      breadCrumbsAboveTitle={[{ title: 'Mass Messages' }]}
      actions={
        <Button type="primary" onClick={() => history.push('/Messaging/mass-messaging/new-message')}>
          New Message
        </Button>
      }
    >
      {/* Stats cards */}
      <div className="mass-messaging-2__stats">
        <Card bordered={false} className="w-100-P flex-row-center align-left">
          <Statistic
            title="Messages Sent"
            value={messagesSent}
            className="ff-poppins"
            valueStyle={{ fontWeight: 700 }}
          />
          <span className="mass-messaging-2__stat-description">
            Across {totalMassMessages} mass message{totalMassMessages !== 1 ? 's' : ''}
          </span>
        </Card>

        <Card bordered={false} className="w-100-P flex-row-center align-left">
          <Statistic
            title="Plan Limit"
            value={planLimit}
            className="ff-poppins"
            valueStyle={{ fontWeight: 700 }}
          />
          <div className="mass-messaging-2__stat-progress">
            <span className="mass-messaging-2__stat-description">Percentage of limit used</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Progress
                percent={limitUsedPercent}
                showInfo={false}
                size="small"
                strokeColor="#3aa4fc"
                style={{ width: 100, margin: 0 }}
              />
              <span className="mass-messaging-2__stat-description">{limitUsedPercent}%</span>
            </div>
          </div>
        </Card>

        <Card bordered={false} className="w-100-P flex-row-center align-left">
          <Statistic
            title="Cost"
            value={cost}
            prefix="$"
            className="ff-poppins"
            valueStyle={{ fontWeight: 700 }}
          />
          <span className="mass-messaging-2__stat-description">
            Messages beyond your limit are billed at $0.07 per message sent
          </span>
        </Card>
      </div>

      {/* Filters */}
      <div className="mass-messaging-2__toolbar">
        <Input
          placeholder="Search"
          prefix={<FontAwesomeIcon icon={['far', 'search']} style={{ color: '#bfbfbf' }} />}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          allowClear
          style={{ maxWidth: 240 }}
        />
        <DatePicker.RangePicker
          placeholder={['Start date', 'End date']}
          value={dateRange}
          onChange={val => {
            setDateRange(val);
            setPage(1);
          }}
          style={{ maxWidth: 280 }}
        />
      </div>

      {/* Table */}
      <Table
        className="mass-messaging-table"
        dataSource={paginatedMessages}
        columns={columns}
        rowKey={record => record.id}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: filteredMessages.length,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        onRow={record => {
          const isTerminal = TERMINAL_STATUSES.has(record.status);
          return {
            onClick: isTerminal
              ? undefined
              : () => history.push(`/Messaging/mass-messaging/${record.id}`),
            style: { cursor: isTerminal ? 'default' : 'pointer' },
          };
        }}
        size="middle"
      />

      {/* Cancel modal */}
      <Modal
        open={!!cancelTarget}
        title="Cancel Mass Message"
        onCancel={() => setCancelTarget(null)}
        onOk={handleCancel}
        okText="Cancel Message"
        okButtonProps={{ danger: true, loading: cancelMutation.isLoading }}
      >
        <p>Are you sure you want to cancel this scheduled message?</p>
      </Modal>
    </StandardLayout>
  );
};

export default MassMessaging2;
